<script setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import LibraryCatalogFilters from './LibraryCatalogFilters.vue';
  import LibraryCatalogTable from './LibraryCatalogTable.vue';

  const { t } = useI18n();
  const i18nScope = ref('components.library_catalog');
  const libraryCatalogTable = ref(null);

  const props = defineProps({
    roles: {
      type: Array,
      required: true
    },
    networks: {
      type: Array,
      required: true
    },
    lang: {
      type: String,
      required: true
    },
    defaultNetwork: {
      type: Object,
      default: null
    },
    currentLibrary: {
      type: Object,
      default: null
    },
    audiences: {
      type: Array,
      default: () => []
    }
  });

  const currentLibraryId = computed(() => props.currentLibrary?.id || null);

  const filterData = ref({
    searchQuery: '',
    selectedLanguage: null,
    selectedFormat: null,
    selectedCategory: null,
    selectedAudience: null,
    startDate: null,
    endDate: null,
    acquisitionStartDate: null,
    acquisitionEndDate: null
  });

  const visibleColumns = ref({
    title: true,
    isbn: true,
    author: true,
    publisher: false,
    category: false,
    format: false,
    language: false,
    audience: false,
    acquisitionDate: false,
    expirationDate: false,
    licenses: true,
    totalLoans: true,
    reservations: true
  });

  const handleFilterChange = () => {
    if (libraryCatalogTable.value) {
      libraryCatalogTable.value.clearLicenses();
      setTimeout(() => {
        libraryCatalogTable.value.fetchLicenses();
      }, 300);
    }
  };

  const handleSearch = (query) => {
    filterData.value.searchQuery = query;
    handleFilterChange();
  };

  /**
   * Handles exporting the library catalog data
   * @param {string} type - Export type: 'licenses' for catalog report or 'offers' for offers report
   * 
   * type is either 'licenses' or 'offers'
   * licenses is the report of the catalog that the user is looking at on screen
   * offers is a report of all the offers in the catalog from all the titles on screen
   */
  const handleExport = async (type = 'licenses') => {
    if (!props.currentLibrary) {
      alert(t(`${i18nScope.value}.export_error_no_library`));
      return;
    }

    const library = props.currentLibrary;
    const params = new URLSearchParams({
      library_id: library.id
    });

    if (filterData.value.startDate) params.append('start_date', filterData.value.startDate);
    if (filterData.value.endDate) params.append('end_date', filterData.value.endDate);
    if (filterData.value.selectedLanguage) params.append('language', filterData.value.selectedLanguage);
    if (filterData.value.selectedFormat) params.append('book_format', filterData.value.selectedFormat);
    if (filterData.value.selectedCategory) params.append('category', filterData.value.selectedCategory);
    if (filterData.value.selectedAudience) params.append('audience', filterData.value.selectedAudience);
    if (filterData.value.acquisitionStartDate) params.append('acquisition_start_date', filterData.value.acquisitionStartDate);
    if (filterData.value.acquisitionEndDate) params.append('acquisition_end_date', filterData.value.acquisitionEndDate);
    params.append('export_type', type);

    try {
      const response = await fetch(`/api/library_catalog/export?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(t(`${i18nScope.value}.export_success`));
      } else {
        alert(t(`${i18nScope.value}.export_error`));
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(t(`${i18nScope.value}.export_error`));
    }
  };
</script>

<template>
  <div class="library-catalog-container">
    <div class="justify-content-start">
      <LibraryCatalogFilters
        v-model:searchQuery="filterData.searchQuery"
        v-model:selectedLanguage="filterData.selectedLanguage"
        v-model:selectedFormat="filterData.selectedFormat"
        v-model:selectedCategory="filterData.selectedCategory"
        v-model:selectedAudience="filterData.selectedAudience"
        v-model:startDate="filterData.startDate"
        v-model:endDate="filterData.endDate"
        v-model:acquisitionStartDate="filterData.acquisitionStartDate"
        v-model:acquisitionEndDate="filterData.acquisitionEndDate"
        :audiences="audiences"
        :lang="lang"
        :libraryId="currentLibraryId"
        v-model:visibleColumns="visibleColumns"
        @filter-change="handleFilterChange"
        @search="handleSearch"
        @export="handleExport('licenses')"
        @export-offers="handleExport('offers')"
      />

      <hr>

      <LibraryCatalogTable
        ref="libraryCatalogTable"
        :searchQuery="filterData.searchQuery"
        :selectedLanguage="filterData.selectedLanguage"
        :selectedFormat="filterData.selectedFormat"
        :selectedCategory="filterData.selectedCategory"
        :selectedAudience="filterData.selectedAudience"
        :startDate="filterData.startDate"
        :endDate="filterData.endDate"
        :acquisitionStartDate="filterData.acquisitionStartDate"
        :acquisitionEndDate="filterData.acquisitionEndDate"
        :currentLibrary="currentLibrary"
        :visibleColumns="visibleColumns"
        class="my-4"
      />
    </div>
  </div>
</template>

<style lang="scss">
  @import '../../../../node_modules/bootstrap/scss/bootstrap.scss';
  @import '../../../../node_modules/bootstrap-vue-next/dist/bootstrap-vue-next';
  @import '../../stylesheets/catalog-publication.scss';

  .library-catalog-container {
    padding: 20px 0;
  }
</style>

<i18n src="../../locales/en.json" global></i18n>
<i18n src="../../locales/fr.json" global></i18n>
<i18n src="../../locales/es.json" global></i18n>
<i18n src="../../locales/it.json" global></i18n>
<i18n src="../../locales/pt.json" global></i18n>
